import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Modal from '../../../../components/Common/Modal';
import {
  CloseIcon,
  ImageViewContainer,
  ImageViewLeftSection,
  ImageViewRightSection,
  ImageViewRightSectionImage,
  ImageViewRightSectionNext,
  ImageViewRightSectionPrev,
  VideoViewRightSectionImage,
} from './ImageDescriptionModal.styles';
import { CloseIconGrey } from '@/assets/svgExports/CloseIconGrey';
import { ImageModalBackArrow } from '@/assets/svgExports/ImageModalBackArrow';
import { ImageModalLeftArrow } from '@/assets/svgExports/ImageModalLeftArrow';
import { LeftArrowImageModalMobile } from '@/assets/svgExports/LeftArrowImageModalMobile';
import { useState } from 'react';
import { deviceWidth } from '@/utils/deviceWidth';
import { MAX_MOBILE_WIDTH, MEDIA_TYPES } from '@/utils/constants';
import useIsComponentMounted from '@/utils/useIsComponentMounted';
import useSetImagesDescriptionModal from '../../../../hooks/useSetImagesDescriptionModal';
import YouTubeViewer from '../ImageDescriptionModal/YouTubeViewer';
import { isThemeCustomizableForStore } from 'src/utils/isThemeCustomizableForStore';
import { SidePanelImages } from '../DesktopImageComponent/SidePanelImages';
import { MediaContainer } from 'src/components/ProductMediaRenderer/MediaContainer';
import { getThumbnailImageDimensions } from '../DesktopImageComponent/utils';

const SCROLL_ACTION = {
  NEXT: 'NEXT',
  PREV: 'PREV',
};

const getImageDimensionsAccordingToAspectRatio = (isMobile, aspectRatio) => {
  if (isMobile) {
    const imageDimensionsObj = {
      height: '360px',
      width: '100vw',
    };
    if (aspectRatio > 100) {
      imageDimensionsObj.width = '100vw';
      imageDimensionsObj.height = '202px';
    }
    if (aspectRatio < 100) {
      imageDimensionsObj.width = '202px';
      imageDimensionsObj.height = '360px';
    }
    return imageDimensionsObj;
  }
  const imageDimensionsObj = {
    height: '600px',
    width: '600px',
  };
  if (aspectRatio > 100) {
    imageDimensionsObj.width = '600px';
    imageDimensionsObj.height = '338px';
  }
  if (aspectRatio < 100) {
    imageDimensionsObj.width = '338px';
    imageDimensionsObj.height = '600px';
  }
  return imageDimensionsObj;
};

// const getPlayIconPosition = (aspectRatio, isCustomWebsite) => {
//   if (isCustomWebsite && aspectRatio > 100) return '4px 0px 0px 12px';
//   if (isCustomWebsite && aspectRatio < 100) return '12px 0px 0px 2px';
//   return '12px 0px 0px 12px';
// };

function ImageDescriptionModal(props) {
  const {
    handleShowImageDescriptionModal,
    showImageDescModal,
    productData,
    activeIdx,
    mainImageIndexFromPdp,
  } = props;
  const isMobile = deviceWidth <= MAX_MOBILE_WIDTH;
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const selectedPanelToView = useRef();
  const isComponentMounted = useIsComponentMounted();
  const { theme } = useSelector((state) => ({
    theme: state.storeReducer.store.theme,
  }));

  useEffect(() => {
    selectedPanelToView.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
  }, [mainImageIndex]);

  useEffect(() => {
    setMainImageIndex(mainImageIndexFromPdp);
  }, [mainImageIndexFromPdp, activeIdx]);

  const customThemeFlag = isThemeCustomizableForStore();

  const imageDimensions = getImageDimensionsAccordingToAspectRatio(
    isMobile,
    theme?.product_aspect_ratio
  );
  const thumbnailImageDimensions = getThumbnailImageDimensions(
    theme?.product_aspect_ratio
  );
  // const playIconPosition = getPlayIconPosition(
  //   theme?.product_aspect_ratio,
  //   theme?.custom_theme_flag
  // );

  const images = useSetImagesDescriptionModal({
    productData,
    activeIdx,
    showImageDescModal,
  });

  function handleNextPrevIcon(clickType) {
    if (clickType === SCROLL_ACTION.NEXT) {
      if (mainImageIndex < images?.length - 1) {
        setMainImageIndex((prevIndex) => prevIndex + 1);
      } else {
        setMainImageIndex(0);
      }
    } else {
      if (mainImageIndex > 0) {
        setMainImageIndex((prevIndex) => prevIndex - 1);
      } else {
        setMainImageIndex(images?.length - 1);
      }
    }
  }

  function handleLeftPanelImageClick(index) {
    setMainImageIndex(index);
  }

  return (
    <Modal
      visible={showImageDescModal}
      className="bottom-modal imageDescriptionModal"
      animation={'slideUp'}
      height={isMobile ? '100vh' : 200}
      onClose={handleShowImageDescriptionModal}
    >
      <ImageViewContainer>
        <ImageViewLeftSection>
          <SidePanelImages
            sidePanelImages={images}
            thumbnailImageDimensions={thumbnailImageDimensions}
            mainImage={images[mainImageIndex]}
            returnIndexOnSelect={true}
            handleSidePanelImage={(index) => handleLeftPanelImageClick(index)}
          />
        </ImageViewLeftSection>
        <ImageViewRightSection>
          <ImageViewRightSectionPrev
            className="tw-z-10"
            onClick={() => {
              handleNextPrevIcon(SCROLL_ACTION.PREV);
            }}
          >
            {isMobile ? <LeftArrowImageModalMobile /> : <ImageModalLeftArrow />}
          </ImageViewRightSectionPrev>
          <MediaContainer width={imageDimensions.width}>
            {images[mainImageIndex]?.media_type === MEDIA_TYPES.YOUTUBE_VIDEO ? (
              showImageDescModal && (
                <YouTubeViewer linkUrl={images[mainImageIndex]?.image_url} />
              )
            ) : images[mainImageIndex]?.media_type !== MEDIA_TYPES.VIDEO ? (
              <ImageViewRightSectionImage
                src={images[mainImageIndex]?.image_url}
                imageDimensions={imageDimensions}
                isCustomWebsite={customThemeFlag}
              />
            ) : (
              <VideoViewRightSectionImage
                videoDimensions={imageDimensions}
                isCustomWebsite={customThemeFlag}
              >
                <video
                  src={images[mainImageIndex]?.image_url || ''}
                  controls
                  className="tw-h-full tw-w-full tw-object-contain"
                />
              </VideoViewRightSectionImage>
            )}
          </MediaContainer>

          <ImageViewRightSectionNext
            onClick={() => {
              handleNextPrevIcon(SCROLL_ACTION.NEXT);
            }}
          >
            {isMobile ? <LeftArrowImageModalMobile /> : <ImageModalLeftArrow />}
          </ImageViewRightSectionNext>
        </ImageViewRightSection>
        <CloseIcon onClick={handleShowImageDescriptionModal}>
          {isMobile && isComponentMounted ? <ImageModalBackArrow /> : <CloseIconGrey />}
        </CloseIcon>
      </ImageViewContainer>
    </Modal>
  );
}

export default ImageDescriptionModal;
